<script setup lang="ts">
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { ThemeMode } from '@/stores/settings'
import { Compass, Home, Laptop, ListVideo, Moon, Search, Settings, Sun, UserRound } from '@lucide/vue'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSettingsStore } from '@/stores/settings'
import BrandMark from './BrandMark.vue'

const route = useRoute()
const settings = useSettingsStore()
const immersive = computed(() => Boolean(route.meta.immersive))
const desktopNav = [
    { label: '首页', to: '/', icon: Compass },
    { label: '搜索', to: '/search', icon: Search },
    { label: '收藏/历史', to: '/library', icon: ListVideo },
    { label: '我的', to: '/me', icon: UserRound },
]
const mobileNav = [
    { label: '首页', to: '/', icon: Home },
    { label: '搜索', to: '/search', icon: Search },
    { label: '收藏/历史', to: '/library', icon: ListVideo },
    { label: '我的', to: '/me', icon: UserRound },
]
const cachedViews = ['HomeView', 'SearchView', 'DetailView', 'LibraryView', 'MeView']
const themeOptions: Array<{ id: ThemeMode, label: string, icon: typeof Moon }> = [
    { id: 'dark', label: '深色模式', icon: Moon },
    { id: 'system', label: '跟随系统', icon: Laptop },
    { id: 'light', label: '浅色模式', icon: Sun },
]

function viewCacheKey(currentRoute: RouteLocationNormalizedLoaded) {
    if (currentRoute.name === 'play') {
        return `play:${String(currentRoute.params.source)}:${String(currentRoute.params.id)}:${String(currentRoute.query.watch || '')}`
    }
    return currentRoute.name === 'detail'
        ? `detail:${String(currentRoute.params.source)}:${String(currentRoute.params.id)}`
        : String(currentRoute.name || currentRoute.path)
}

function active(to: string) {
    return to === '/' ? route.path === '/' : route.path.startsWith(to)
}
</script>

<template>
    <div class="min-h-screen text-[var(--c-text)]">
        <header v-if="!immersive" class="top-bar fixed inset-x-0 top-0 z-40 h-16 border-b border-[var(--c-border)] sm:h-18">
            <div class="mx-auto h-full max-w-[1560px] flex items-center px-5 lg:px-12 sm:px-8">
                <BrandMark />
                <nav class="ml-12 hidden h-9 items-center gap-1 rounded-full bg-[var(--c-fill-subtle)] p-1 md:flex">
                    <RouterLink
                        v-for="item in desktopNav"
                        :key="item.to"
                        :to="item.to"
                        class="relative h-full flex items-center gap-2 rounded-full px-4 text-sm font-600 transition duration-300 focus-ring"
                        :class="active(item.to) ? 'bg-[var(--c-surface-raised)] text-[var(--c-text)]' : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)]'"
                    >
                        <component :is="item.icon" class="size-3.5" :stroke-width="active(item.to) ? 2.1 : 1.7" />{{ item.label }}
                    </RouterLink>
                </nav>
                <div class="ml-auto flex items-center gap-2">
                    <div class="relative grid-cols-3 hidden h-9 w-28 border border-[var(--c-border)] rounded-full bg-[var(--c-fill-subtle)] p-0.5 md:grid" aria-label="界面主题">
                        <span class="pointer-events-none absolute bottom-0.5 left-0.5 top-0.5 w-[calc((100%-0.25rem)/3)] border border-[var(--c-border)] rounded-full bg-[var(--c-surface-raised)] transition-transform duration-300" :style="{ transform: `translateX(${themeOptions.findIndex(item => item.id === settings.theme) * 100}%)` }" />
                        <button v-for="item in themeOptions" :key="item.id" type="button" class="relative z-1 grid place-items-center rounded-full text-[var(--c-text-muted)] transition hover:text-[var(--c-text)] focus-ring" :class="settings.theme === item.id && 'text-[var(--c-text)]'" :aria-label="item.label" :title="item.label" :aria-pressed="settings.theme === item.id" @click="settings.theme = item.id">
                            <component :is="item.icon" class="size-4.5" />
                        </button>
                    </div>
                    <RouterLink to="/settings" class="grid size-11 place-items-center rounded-full text-[var(--c-text-soft)] transition hover:text-[var(--c-text)] focus-ring" aria-label="设置">
                        <Settings class="size-5" />
                    </RouterLink>
                </div>
            </div>
        </header>

        <main :class="!immersive && 'pt-16 sm:pt-18'">
            <RouterView v-slot="{ Component, route: currentRoute }">
                <KeepAlive :include="cachedViews" :max="12">
                    <component :is="Component" :key="viewCacheKey(currentRoute)" />
                </KeepAlive>
            </RouterView>
        </main>

        <nav v-if="!immersive" class="bottom-bar fixed inset-x-3 bottom-[max(.75rem,env(safe-area-inset-bottom))] z-40 grid grid-cols-4 h-16 border border-[var(--c-border)] rounded-[999px] p-1.5 md:hidden">
            <RouterLink
                v-for="item in mobileNav"
                :key="item.to"
                :to="item.to"
                class="relative h-full flex flex-col items-center justify-center gap-1 rounded-full text-xs font-600 transition duration-150 active:scale-[0.96] focus-ring"
                :class="active(item.to) ? 'bg-[var(--c-surface-raised)] text-[var(--accent)] shadow-sm shadow-[var(--c-shadow)]' : 'text-[var(--c-text-muted)]'"
            >
                <component :is="item.icon" class="size-[19px]" :stroke-width="active(item.to) ? 2.2 : 1.6" />
                <span>{{ item.label }}</span>
            </RouterLink>
        </nav>
    </div>
</template>

<style scoped>
.top-bar,
.bottom-bar {
    background: linear-gradient(135deg, var(--c-glass-highlight), transparent 38%), var(--c-glass);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    transform: translateZ(0);
}

.top-bar {
    background: linear-gradient(135deg, var(--c-glass-highlight), transparent 38%), var(--c-top-bar);
    box-shadow: none;
}

.bottom-bar {
    box-shadow:
        0 12px 32px rgb(0 0 0 / 14%),
        inset 0 1px 0 var(--c-glass-highlight);
}

@media (prefers-reduced-transparency: reduce) {
    .top-bar,
    .bottom-bar {
        background: var(--c-surface-raised);
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
    }
}
</style>
